import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { useAppointmentStore } from '@/store/appointmentStore';
import { useAuthStore } from '@/store/authStore';
import { usePracticeStore } from '@/store/practiceStore';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';

// Importazione necessaria per accedere allo store al di fuori di un componente
const usePracticeStoreGetState = usePracticeStore.getState;

interface CreateAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practiceId: string;
  clientId?: string;
  clientName?: string; // Nome del cliente per auto-compilazione titolo
  interventionLocation?: string; // Luogo intervento per auto-compilazione titolo (preferito)
  clientAddress?: string; // Fallback: indirizzo cliente (deprecato)
  onSuccess?: () => void;
}

export default function CreateAppointmentDialog({
  open,
  onOpenChange,
  practiceId,
  clientId: initialClientId,
  clientName,
  interventionLocation,
  clientAddress,
  onSuccess,
}: CreateAppointmentDialogProps) {
  const [formData, setFormData] = useState({
    clientId: initialClientId || '',
    userId: '',
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    location: '',
    status: 'SCHEDULED' as 'SCHEDULED' | 'COMPLETED' | 'CANCELLED',
  });
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [endTimeManuallyEdited, setEndTimeManuallyEdited] = useState(false);

  const { createAppointment, loading } = useAppointmentStore();
  const { user: currentUser } = useAuthStore();
  const { fetchPractice } = usePracticeStore();
  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [practiceTitle, setPracticeTitle] = useState<string>('');

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (open) {
      // Fetch users (endpoint accessibile a tutti gli utenti autenticati)
      apiClient.get('/users/assignable')
        .then((response) => {
          console.log('Users API response (appointments):', response.data);
          const usersData = response.data?.data || [];
          console.log('Parsed users (appointments):', usersData);
          setUsers(usersData);
        })
        .catch((error) => {
          console.error('Error fetching users (appointments):', error);
          // Se fallisce, usa almeno l'utente corrente
          if (currentUser) {
            setUsers([{
              id: currentUser.id,
              firstName: currentUser.firstName || '',
              lastName: currentUser.lastName || '',
              email: currentUser.email || '',
            }]);
          }
        });
      
      // Fetch pratica per ottenere il titolo
      if (practiceId) {
        fetchPractice(practiceId)
          .then(() => {
            // La pratica viene salvata nello store, leggiamola da lì
            setTimeout(() => {
              const practice = usePracticeStoreGetState().currentPractice;
              if (practice) {
                setPracticeTitle(practice.title || '');
              }
            }, 100);
          })
          .catch((error) => {
            console.error('Error fetching practice:', error);
            setPracticeTitle('');
          });
      }
      
      // Reset form quando si apre
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000); // +1 ora
      
      setFormData({
        clientId: initialClientId || '',
        userId: currentUser?.id || '',
        title: '',
        description: '',
        startTime: formatDateTimeLocal(now),
        endTime: formatDateTimeLocal(endTime),
        location: '',
        status: 'SCHEDULED',
      });
      setTitleManuallyEdited(false);
      setEndTimeManuallyEdited(false);
      setPracticeTitle('');
    }
  }, [open, currentUser, initialClientId, practiceId, fetchPractice]);

  // Aggiorna automaticamente endTime quando cambia startTime (+30 minuti)
  useEffect(() => {
    if (formData.startTime && !endTimeManuallyEdited) {
      const startDate = new Date(formData.startTime);
      const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // +30 minuti
      setFormData(prev => ({ ...prev, endTime: formatDateTimeLocal(endDate) }));
    }
  }, [formData.startTime, endTimeManuallyEdited]);

  // Auto-compila titolo quando si apre il dialog o quando cambia clientName/practiceTitle
  // Formato: Tipo - NomeCliente - TitoloPraticaPulito - Indirizzo
  useEffect(() => {
    if (!titleManuallyEdited && open) {
      let autoTitle = 'Incontro';
      
      // Aggiungi nome cliente se presente
      if (clientName) {
        autoTitle = `${autoTitle} - ${clientName}`;
      }
      
      // Aggiungi titolo pratica se presente, rimuovendo il nome cliente se presente
      if (practiceTitle) {
        let cleanPracticeTitle = practiceTitle;
        // Rimuovi il nome cliente dal titolo pratica se presente
        if (clientName && practiceTitle.includes(clientName)) {
          // Rimuovi il nome cliente e eventuali separatori
          cleanPracticeTitle = practiceTitle
            .replace(new RegExp(`\\s*-\\s*${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), '')
            .replace(new RegExp(`${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*-\\s*`, 'gi'), '')
            .replace(new RegExp(`\\s*${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'gi'), '')
            .trim();
          // Rimuovi eventuali doppi separatori o separatori iniziali/finali
          cleanPracticeTitle = cleanPracticeTitle.replace(/\s*-\s*-\s*/g, ' - ').replace(/^\s*-\s*|\s*-\s*$/g, '').trim();
        }
        if (cleanPracticeTitle) {
          autoTitle = `${autoTitle} - ${cleanPracticeTitle}`;
        }
      }
      
      // Aggiungi luogo intervento o indirizzo se presente (preferenza a interventionLocation)
      const location = interventionLocation || clientAddress;
      if (location) {
        autoTitle = `${autoTitle} - ${location}`;
      }
      
      setFormData(prev => ({ ...prev, title: autoTitle }));
    }
  }, [clientName, interventionLocation, clientAddress, practiceTitle, open, titleManuallyEdited]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.startTime) {
      toast.error('Titolo e data/ora di inizio sono obbligatori');
      return;
    }

    if (!formData.clientId) {
      toast.error('Cliente è obbligatorio');
      return;
    }

    // Validazione date retroattive
    const startTime = new Date(formData.startTime);
    const now = new Date();
    if (startTime < now) {
      toast.error('La data/ora di inizio non può essere nel passato');
      return;
    }

    if (formData.endTime) {
      const endTime = new Date(formData.endTime);
      if (endTime < startTime) {
        toast.error('La data/ora di fine deve essere successiva a quella di inizio');
        return;
      }
    }

    try {
      await createAppointment({
        practiceId,
        clientId: formData.clientId,
        userId: formData.userId || undefined,
        title: formData.title,
        description: formData.description || undefined,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: formData.endTime ? new Date(formData.endTime).toISOString() : undefined,
        location: formData.location || undefined,
        status: formData.status,
      });

      toast.success('Appuntamento creato con successo');
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore nella creazione dell\'appuntamento');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo Appuntamento</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Titolo *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                setTitleManuallyEdited(true);
              }}
              placeholder="Es: Incontro con cliente per ristrutturazione"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrizione dettagliata dell'appuntamento..."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startTime">Data/Ora Inizio *</Label>
              <Input
                id="startTime"
                type="datetime-local"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                min={formatDateTimeLocal(new Date())}
                required
              />
            </div>

            <div>
              <Label htmlFor="endTime">Data/Ora Fine</Label>
              <Input
                id="endTime"
                type="datetime-local"
                value={formData.endTime}
                onChange={(e) => {
                  setFormData({ ...formData, endTime: e.target.value });
                  setEndTimeManuallyEdited(true); // L'utente ha modificato manualmente
                }}
                min={formData.startTime || formatDateTimeLocal(new Date())}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="location">Luogo</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Es: Ufficio, Indirizzo cliente..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Stato</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value as any })}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SCHEDULED">Programmato</SelectItem>
                  <SelectItem value="COMPLETED">Completato</SelectItem>
                  <SelectItem value="CANCELLED">Annullato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="userId">Assegnatario</Label>
              <Combobox
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName}`,
                  description: user.email || undefined,
                }))}
                value={formData.userId}
                onValueChange={(value) => setFormData({ ...formData, userId: value })}
                placeholder="Seleziona assegnatario (opzionale)"
                searchPlaceholder="Cerca utente..."
                emptyMessage="Nessun utente trovato"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={loading || !formData.title || !formData.startTime || !formData.clientId}>
              {loading ? 'Creazione...' : 'Crea Appuntamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

